// Single cross-app origin for Stripe price-ID *fallback* literals. Each app still layers its own
// env override (NEXT_PUBLIC_* on web, STRIPE_*_PRICE_ID on api) on top of these, so these literals
// live in exactly one place instead of being duplicated across apps/web and apps/api.
export const STRIPE_PRICE_ID_FALLBACKS = {
  individual: 'price_1SoxeD98Jn87vuFevPZtSYre',
  startup: 'price_1Soxgn98Jn87vuFebMlFzYNs',
} as const;

// Per-turn pagination bounds for the conversation-detail surface, single-sourced cross-app so the
// web page-size and the API route's default/max cannot silently drift. The conversation route
// (GET /api/organizations/[id]/conversations/[convoId]) defaults ?limit to CONVERSATION_DEFAULT_TURN_LIMIT
// and validates it against CONVERSATION_MAX_TURN_LIMIT; the web session-detail page pages by
// CONVERSATION_DEFAULT_TURN_LIMIT via offset, so a single request never exceeds the route's max.
export const CONVERSATION_DEFAULT_TURN_LIMIT = 50;
export const CONVERSATION_MAX_TURN_LIMIT = 200;

// =============================================================================
// Pure cross-app string/format helpers
// =============================================================================
// These are dependency-free, client-safe pure functions that were previously
// duplicated byte-for-byte in apps/web/src/lib/utils.ts and
// apps/api/src/lib/utils/index.ts and kept aligned only by golden parity tests.
// @sentrial/config is the proven cross-app home (already imported by both apps),
// so they live here in exactly one place — no drift possible. Each app keeps only
// its own thin wrapper where the surface behavior genuinely differs (e.g. the
// null/NaN cost sentinel).

/**
 * Convert a string into a URL-safe slug: lowercases, replaces any run of
 * non-alphanumeric characters with a single hyphen, and trims leading/trailing
 * hyphens.
 *
 *   slugify('My Agent!')  // => 'my-agent'
 *   slugify('a_b c')      // => 'a-b-c'
 */
export function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * RFC-4180 CSV cell escaper (write direction). Quotes a cell only when it contains a
 * comma, a double-quote, or a newline (`\n`/`\r`), and doubles any embedded double-quotes.
 * null/undefined → empty string.
 *
 * Cells feed user/agent-controlled text (dataset values, signal labels, userInput, ...) so we
 * also neutralize spreadsheet formula/DDE injection: a cell whose first char is a formula trigger
 * (`=` `+` `-` `@`, TAB, or CR) is prefixed with a single quote so Excel/Sheets treat it as text
 * instead of evaluating it. This CSV-injection guard is load-bearing — do not drop the prefix regex.
 */
export function escapeCsv(value: unknown): string {
  if (value == null) return '';
  let str = String(value);
  if (/^[=+\-@\t\r]/.test(str)) {
    str = `'${str}`;
  }
  if (str.includes(',') || str.includes('"') || str.includes('\n') || str.includes('\r')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

/**
 * Serialize a matrix of cells into a CSV string, escaping each cell via {@link escapeCsv}.
 * Rows are joined with `\n`; cells within a row with `,`.
 */
export function rowsToCsv(rows: unknown[][]): string {
  return rows.map((row) => row.map(escapeCsv).join(',')).join('\n');
}

/**
 * Numeric core of the shared 4-decimal dollar-cost format. Returns `$${n.toFixed(4)}`
 * for a finite number and `null` for null/undefined/NaN, so each app layers its own
 * null sentinel on top (web '$0.0000', api 'N/A') without duplicating the numeric format.
 */
export function formatCostUsd4(cost: number | null | undefined): string | null {
  if (cost == null || Number.isNaN(cost)) return null;
  return `$${cost.toFixed(4)}`;
}

/**
 * Canonical "custom-metric name → snake_case key" transform used to READ and WRITE the
 * `metricScores.customMetrics[...]` JSON blob. Lowercases, collapses whitespace runs to a
 * single underscore, then strips every remaining non-`[a-z0-9_]` character (so punctuation
 * like `Avg. Latency` maps to `avg_latency`, not `avg._latency`). This is the exact chain the
 * scorer uses when keying stored scores, so every reader MUST use it too or a punctuated metric
 * name silently reads back "No data". Single-sourced here to prevent the four previously
 * hand-rolled copies from drifting.
 */
export function metricScoreKey(name: string): string {
  return name
    .toLowerCase()
    .replace(/\s+/g, '_')
    .replace(/[^a-z0-9_]/g, '');
}

/**
 * Canonical agent display-name derivation, shared cross-app so the web `agentDisplayName()` and the
 * api `agentLabel()` cannot drift. Prefers the human `displayName`, falls back to the internal
 * `name`, then to `fallback` ('Unknown' by default). Preserves `||` (not `??`) falsiness so an
 * empty-string/null displayName still falls through to name.
 */
export function agentDisplayName(
  agent?: { name?: string | null; displayName?: string | null } | null,
  fallback = 'Unknown'
): string {
  return agent?.displayName || agent?.name || fallback;
}
